import {
  type TenantClientConfig,
  type TenantSupabaseClient,
  createTenantClient,
} from './client.js';

export { createTenantClient };
export type { TenantClientConfig, TenantSupabaseClient };

declare global {
  interface ImportMetaEnv {
    readonly VITE_SUPABASE_URL?: string;
    readonly VITE_SUPABASE_ANON_KEY?: string;
  }
  interface ImportMeta {
    readonly env: ImportMetaEnv;
  }
}

type SazeoImportMetaEnv = {
  readonly VITE_SUPABASE_URL?: string;
  readonly VITE_SUPABASE_ANON_KEY?: string;
};

type SazeoGlobalEnv = typeof globalThis & {
  readonly __SAZEO_VITE_ENV__?: SazeoImportMetaEnv;
};

let memoizedClient: TenantSupabaseClient | undefined;

/**
 * Read a required env var from `import.meta.env` (Vite) with a
 * `process.env` fallback for Node/test contexts.
 *
 * Reason for the literal `import.meta.env.VITE_X` pattern below: Vite's
 * static replacement only fires on the EXACT literal access. Aliasing
 * (`const meta = import.meta; meta.env?.VITE_X`) leaves the access
 * untransformed at runtime, so the value is always undefined in the
 * production bundle even when the .env file is set correctly.
 */
function readEnv(key: 'VITE_SUPABASE_URL' | 'VITE_SUPABASE_ANON_KEY'): string | undefined {
  let fromVite: string | undefined;
  if (key === 'VITE_SUPABASE_URL') {
    fromVite = import.meta.env?.VITE_SUPABASE_URL;
  } else {
    fromVite = import.meta.env?.VITE_SUPABASE_ANON_KEY;
  }
  if (fromVite) return fromVite;

  const fromGlobal = (globalThis as SazeoGlobalEnv).__SAZEO_VITE_ENV__?.[key];
  if (fromGlobal) return fromGlobal;

  // Node/test fallback — Vitest exposes process.env; browsers do not.
  if (typeof process !== 'undefined' && process.env) {
    return process.env[key];
  }
  return undefined;
}

/**
 * Return a memoized tenant Supabase client, configured from env vars.
 *
 * Reads `VITE_SUPABASE_URL` + `VITE_SUPABASE_ANON_KEY`. Throws a clear error
 * if either is missing — we want loud, early failures, not mystery 401s.
 *
 * Browser-safe: uses `import.meta.env` (Vite) with a Node fallback for tests.
 */
export function getTenantClient(): TenantSupabaseClient {
  if (memoizedClient) return memoizedClient;

  const url = readEnv('VITE_SUPABASE_URL');
  const anonKey = readEnv('VITE_SUPABASE_ANON_KEY');

  if (!url || !anonKey) {
    throw new Error(
      '[@sazeo/supabase-tenant] Missing env vars: VITE_SUPABASE_URL and/or VITE_SUPABASE_ANON_KEY. ' +
        "Add them to the consuming app's .env (Vite) or the process environment.",
    );
  }

  memoizedClient = createTenantClient({ url, anonKey });
  return memoizedClient;
}

/**
 * Test-only helper — reset the memoized singleton so unit tests can swap
 * env vars between cases. Not part of the public runtime contract.
 *
 * @internal
 */
export function __resetTenantClientForTests(): void {
  memoizedClient = undefined;
}
